/**
 * ID token decoding and the expiry rules the app restores a session by.
 *
 * **Decode, do not verify.** Nothing here checks the signature, deliberately:
 * a restored session is a UI convenience and the API gateway is the authority.
 * See `docs/decisions/0003-ui-internal-persists-one-id-token.md`.
 */

/**
 * How much life a stored token needs before the app will restore from it.
 *
 * Five minutes, matching the SDK's own proactive-refresh lead
 * (`Duration.OFFSET` in `@firebase/auth`). Below it, restoring buys a console
 * that logs the operator out mid-task.
 */
export const RESTORE_FLOOR_MS = 5 * 60 * 1000

/** The claims this app reads off an ID token. */
export interface IdTokenClaims {
  /** Expiry in milliseconds since the epoch. The JWT's `exp` is seconds. */
  expiresAt: number
  /** The `sub` claim, which is the Firebase uid. */
  uid: string
  /** The `email` claim, or null on a token that carries none. */
  email: string | null
}

/**
 * Turn one base64url JWT segment into whatever JSON it holds.
 *
 * @param segment - The payload segment, without padding.
 * @returns The parsed value, or null when the segment is not base64url JSON.
 */
function parseSegment(segment: string): unknown {
  // base64url swaps two characters and drops the padding; atob wants both back.
  const base64 = segment.replaceAll('-', '+').replaceAll('_', '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')

  try {
    const binary = atob(padded)
    // atob yields one character per byte, so the bytes must be rebuilt before
    // decoding: a name or an email outside ASCII is mojibake otherwise.
    const bytes = Uint8Array.from(
      binary,
      character => character.codePointAt(0) ?? 0
    )

    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

/**
 * Read the three claims this app needs out of a decoded payload.
 *
 * Every claim is checked rather than asserted: the payload comes out of Web
 * Storage, where anything can be written by hand.
 *
 * @param payload - The decoded payload.
 * @returns The claims, or null when a required one is missing or the wrong
 *   type.
 */
function toClaims(payload: unknown): IdTokenClaims | null {
  if (typeof payload !== 'object' || payload === null) {
    return null
  }
  if (!('exp' in payload) || !('sub' in payload)) {
    return null
  }

  const { exp, sub } = payload
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return null
  }
  if (typeof sub !== 'string' || sub === '') {
    return null
  }

  const email =
    'email' in payload && typeof payload.email === 'string'
      ? payload.email
      : null

  return { expiresAt: exp * 1000, uid: sub, email }
}

/**
 * Decode an ID token's claims.
 *
 * @param token - The raw JWT.
 * @returns The claims, or null for anything that is not a three-segment JWT
 *   carrying a numeric `exp` and a non-empty `sub`.
 */
export function decodeIdToken(token: string): IdTokenClaims | null {
  const segments = token.split('.')
  if (segments.length !== 3) {
    return null
  }

  return toClaims(parseSegment(segments[1]))
}

/**
 * Is this token dead?
 *
 * @param claims - Decoded claims.
 * @param now - The current time in milliseconds.
 * @returns True once the token has reached its own `exp`.
 */
export function isExpired(claims: IdTokenClaims, now: number): boolean {
  return claims.expiresAt <= now
}

/**
 * Is this token worth restoring a session from?
 *
 * Stricter than {@link isExpired}: a token inside {@link RESTORE_FLOOR_MS} of
 * its end would restore a console that dies again in a minute.
 *
 * @param claims - Decoded claims.
 * @param now - The current time in milliseconds.
 * @returns True when more than the floor remains.
 */
export function isRestorable(claims: IdTokenClaims, now: number): boolean {
  return claims.expiresAt - now > RESTORE_FLOOR_MS
}
